// Built-in Davey ISOspec pumps (owner via Betty, 2026-10-19: "drawings for some pumps … turned into
// TraceCAD components so I can drop them into some drawings"). One PLAN (top view) and one SIDE
// (elevation) object for every pump in the catalogue tables: 29 CF (bare shaft), 67 CM at 2 pole and
// 45 CM at 4 pole (40 from the extract + 5 CM 150x125-250 added 2026-10-20, one of them estimated) = 141 pumps, 282 objects.
//
// WHAT IS EXACT AND WHAT IS INDICATIVE. Every number the catalogue gives is drawn at that number:
// the position of the inlet and outlet, the connection sizes, H1 and H2, the feet and their bolt
// holes (M1 M2 N1 N2 N3 S1 S2, W), the shaft (A F D L X), and for the CM series the motor feet (H B J K
// N3 N4), the motor length (S T), the centre height (C) and the terminal box (AD). What the catalogue
// does not give is drawn from stated proportions, taken from the catalogue drawing: the volute is
// 0.81 × H1 in radius, the bracket, housing and adapter are fractions of H1 and F, and the flange
// outside diameters are the standard 16 bar (PN16) sizes. So the OUTLINE and the CONNECTION POSITIONS are
// right for a layout; the inside shapes are a picture, not a shop drawing.
//
// Each object is ONE group of plain lines, circles, arcs and two small texts, made like a saved library
// object (centred on 0,0, millimetres). The side object has two pipe ports (inlet, outlet), so the Pipe
// tool (P) starts a pipe from the flange. The plan object has the inlet port and (3.15.6) the outlet port: the outlet
// points up, out of the plan, so a pipe there is drawn as a floor penetration (a VERTICAL port, see pipes/api.ts).
//
// 3.15.6 (owner, 2026-10-20: "the pump sizes, when I change them, did not change those values ... the stud pattern and
// the top plate should get bigger for larger and smaller diameters, same as the faceplate"): pumpRaw draws a pump at
// any inlet and outlet size. The flanges, the bolt circles, the necks and the labels follow the size; the catalogue
// positions (A, H1, H2, the feet, the motor) do not change. src/pipes/pumpRedraw.ts uses it when an end size changes.
import type { Vec } from "../core/geometry";
import type { Entity, Style, TextEntity } from "../doc/entities";
import { defaultTextStyle } from "../doc/document";
import type { LibraryItem } from "./library";
import type { FittingPortData } from "../pipes/api";
import { CENTRE, OUTLINE } from "./fittings";
import { CF_COLUMNS, CF_ROWS, CF_SHAFTS, CM_COLUMNS, CM_ROWS } from "./pumps-data";
import { flangeOf } from "./flanges";

export const PUMP_CATEGORIES = {
  cf: "Pumps · Davey CF (bare shaft)",
  cm2: "Pumps · Davey CM 2 pole (2900 rpm)",
  cm4: "Pumps · Davey CM 4 pole (1450 rpm)"
} as const;

export type PumpView = "plan" | "side";

/** What a pump object says about itself (group meta.pump), for a later parts list. */
export interface PumpInfo {
  maker: "Davey";
  series: "CF" | "CM";
  /** inlet x outlet - impeller, e.g. 125x100-200 */
  model: string;
  view: PumpView;
  inletDn: number;
  outletDn: number;
  /** CM only */
  kW?: number;
  frame?: string;
  poles?: number;
  massKg: number;
  /** Overall length (inlet face to shaft end for a CF, to the motor end for a CM), mm. */
  lengthMm: number;
  /** Every catalogue dimension of the row, by its catalogue letter. */
  dims: Record<string, number>;
  /** A warning to show with the pump, when the catalogue row needs a check. */
  note?: string;
  /** 3.15.6: the inlet and outlet sizes the pump is DRAWN at, when they differ from the catalogue (an end size change). */
  drawnIn?: number;
  drawnOut?: number;
}

// ------------------------------------------------------------------------------------ the rows --

export interface CfRow {
  In: number; Out: number; Imp: number; shaft: number;
  A: number; F: number; H1: number; H2: number; B: number; M1: number; M2: number;
  N1: number; N2: number; N3: number; T1: number; T2: number; W: number; S1: number; S2: number;
  D: number; L: number; X: number; kg: number;
}
export interface CmRow {
  poles: number; In: number; Out: number; Imp: number; kW: number; Frame: string;
  N3: number; B: number; C: number; E: number; H2: number; H: number; J: number; K: number; L: number;
  H1: number; N2: number; N1: number; S1: number; N4: number; S: number; T: number; M2: number; M1: number;
  AD: number | null; kg: number; note: string | null;
}

function toObjects<T>(columns: readonly string[], rows: ReadonlyArray<ReadonlyArray<number | string | null>>): T[] {
  return rows.map((row) => Object.fromEntries(columns.map((c, i) => [c, row[i]])) as T);
}
export const cfRows = (): CfRow[] => toObjects<CfRow>(CF_COLUMNS, CF_ROWS);
export const cmRows = (): CmRow[] => toObjects<CmRow>(CM_COLUMNS, CM_ROWS);

// ------------------------------------------------------------------------------- flange sizes --

// The PN16 flange table moved to ./flanges.ts (3.15.6): the rubber joint on a pipe end uses it too.
export { flangeOf } from "./flanges";

// --------------------------------------------------------------------------------- the builder --

const HIDDEN: Style = { ...OUTLINE, strokeWidth: 1, dash: "dashed", opacity: 0.7 };
const FINE: Style = { ...OUTLINE, strokeWidth: 1 };
const GAP: Style = { ...OUTLINE, strokeWidth: 1, dash: "dotted", opacity: 0.55 };

class Builder {
  readonly entities: Entity[] = [];
  private n = 0;
  constructor(private readonly key: string) {}
  private id(): string {
    return `${this.key}-${++this.n}`;
  }
  line(points: Vec[], closed = false, style: Style = OUTLINE): void {
    this.entities.push({ id: this.id(), type: "polyline", layerId: "pumps", locked: false, hidden: false, points, closed, style: { ...style } });
  }
  rect(x0: number, y0: number, x1: number, y1: number, style: Style = OUTLINE): void {
    this.line([{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }], true, style);
  }
  circle(center: Vec, r: number, style: Style = OUTLINE): void {
    this.entities.push({ id: this.id(), type: "circle", layerId: "pumps", locked: false, hidden: false, center, r, style: { ...style } });
  }
  arc(center: Vec, r: number, startDeg: number, sweepDeg: number, style: Style = OUTLINE): void {
    this.entities.push({ id: this.id(), type: "arc", layerId: "pumps", locked: false, hidden: false, center, r, startDeg, sweepDeg, style: { ...style } });
  }
  /** A one line text centred on (cx, cy). `up` turns it to run up the page (for the thin edge-on inlet flange). */
  textAt(cx: number, cy: number, content: string, size: number, up = false): void {
    const lineH = size * 1.3; // the app draws a line 1.3 × the size high, anchored at its top
    const t: TextEntity = {
      id: this.id(), type: "text", layerId: "pumps", locked: false, hidden: false,
      pos: up ? { x: cx - lineH / 2, y: cy } : { x: cx, y: cy - lineH / 2 },
      rotationDeg: up ? -90 : 0, content,
      textStyle: { ...defaultTextStyle(), size, align: "center" }
    };
    this.entities.push(t);
  }
}

const rad = (deg: number) => (deg * Math.PI) / 180;

/** Label height: the 1.3 line must fit inside the flange thickness, and stay readable on a 1 : 50 plan. */
const labelSize = (flangeT: number) => Math.max(10, Math.min(16, Math.floor((flangeT * 0.8) / 1.3)));
const r1 = (n: number) => Math.round(n * 10) / 10;

/** A circle of `n` bolt holes on a bolt circle, the first at the top. */
function boltHoles(b: Builder, c: Vec, pcd: number, n: number, hole: number): void {
  for (let i = 0; i < n; i++) {
    const a = rad(-90 + (360 * i) / n);
    b.circle({ x: c.x + (pcd / 2) * Math.cos(a), y: c.y + (pcd / 2) * Math.sin(a) }, hole / 2, FINE);
  }
}

/** A slot (key way): a rectangle with half circles at both ends, along x. */
function slot(b: Builder, x0: number, x1: number, yc: number, w: number): void {
  const r = w / 2;
  b.line([{ x: x0 + r, y: yc - r }, { x: x1 - r, y: yc - r }], false, FINE);
  b.line([{ x: x0 + r, y: yc + r }, { x: x1 - r, y: yc + r }], false, FINE);
  b.arc({ x: x0 + r, y: yc }, r, 90, 180, FINE);
  b.arc({ x: x1 - r, y: yc }, r, -90, 180, FINE);
}

// ------------------------------------------------------- the pump end (casing, flanges, feet) --

/** The shared geometry of the casing: the parts that both series draw the same way. */
interface PumpEnd {
  dnIn: number;
  dnOut: number;
  A: number;
  H1: number;
  H2: number;
  M1: number;
  M2: number;
  N1: number;
  N2: number;
  T1: number;
  boltHole: number;
  /** Width of one foot lug across the pump. The CF table gives it (B); for a CM it is N1 - N2. */
  lug: number;
}

function ends(e: PumpEnd) {
  const fi = flangeOf(e.dnIn);
  const fo = flangeOf(e.dnOut);
  const Rv = 0.81 * e.H1; // volute radius, from the catalogue drawing
  const wallO = Math.max(10, 0.1 * e.dnOut);
  const nw = e.dnOut + 2 * wallO; // outlet neck width
  const cw = Math.max(0.8 * e.M1, nw + 10); // casing width along the shaft
  const nI = e.dnIn / 2 + Math.max(10, 0.1 * e.dnIn); // half width of the inlet neck
  return { fi, fo, Rv, nw, cw, nI, xi: e.A - cw / 2, xb: e.A + cw / 2 };
}

/** SIDE: the casing, the two flanges and the foot, with y = 0 on the shaft and the floor at y = H1. */
function sidePumpEnd(b: Builder, e: PumpEnd): void {
  const { fi, fo, Rv, nw, cw, nI, xi } = ends(e);
  const A = e.A;
  // inlet flange and neck, with the bore as hidden lines
  b.rect(0, -fi.od / 2, fi.t, fi.od / 2);
  b.line([{ x: fi.t, y: -nI }, { x: xi, y: -nI }]);
  b.line([{ x: fi.t, y: nI }, { x: xi, y: nI }]);
  b.line([{ x: 0, y: -e.dnIn / 2 }, { x: xi, y: -e.dnIn / 2 }], false, HIDDEN);
  b.line([{ x: 0, y: e.dnIn / 2 }, { x: xi, y: e.dnIn / 2 }], false, HIDDEN);
  // casing and foot as one outline
  const top = -e.H2 + fo.t;
  b.line(
    [
      { x: A - nw / 2, y: top },
      { x: A + nw / 2, y: top },
      { x: A + nw / 2, y: -Rv * 0.8 },
      { x: A + cw / 2, y: -Rv * 0.55 },
      { x: A + cw / 2, y: Rv * 0.55 },
      { x: A + e.M1 / 2, y: e.H1 - e.T1 },
      { x: A + e.M1 / 2, y: e.H1 },
      { x: A - e.M1 / 2, y: e.H1 },
      { x: A - e.M1 / 2, y: e.H1 - e.T1 },
      { x: A - cw / 2, y: Rv * 0.55 },
      { x: A - cw / 2, y: -Rv * 0.55 },
      { x: A - nw / 2, y: -Rv * 0.8 }
    ],
    true
  );
  b.line([{ x: A - e.M1 / 2, y: e.H1 - e.T1 }, { x: A + e.M1 / 2, y: e.H1 - e.T1 }], false, FINE);
  // outlet flange and its bore
  b.rect(A - fo.od / 2, -e.H2, A + fo.od / 2, -e.H2 + fo.t);
  b.line([{ x: A - e.dnOut / 2, y: -e.H2 }, { x: A - e.dnOut / 2, y: -Rv * 0.5 }], false, HIDDEN);
  b.line([{ x: A + e.dnOut / 2, y: -e.H2 }, { x: A + e.dnOut / 2, y: -Rv * 0.5 }], false, HIDDEN);
  // the foot bolt holes (M2 apart)
  for (const sx of [-1, 1]) {
    const x = A + (sx * e.M2) / 2;
    b.line([{ x: x - e.boltHole / 2, y: e.H1 - e.T1 }, { x: x - e.boltHole / 2, y: e.H1 }], false, HIDDEN);
    b.line([{ x: x + e.boltHole / 2, y: e.H1 - e.T1 }, { x: x + e.boltHole / 2, y: e.H1 }], false, HIDDEN);
  }
}

/** PLAN: the casing seen from above. y = 0 on the shaft; the feet and bolt holes are hidden lines. */
function planPumpEnd(b: Builder, e: PumpEnd): void {
  const { fi, fo, Rv, nw, cw, nI, xi } = ends(e);
  const A = e.A;
  // inlet flange (edge on) and neck
  b.rect(0, -fi.od / 2, fi.t, fi.od / 2);
  b.line([{ x: fi.t, y: -nI }, { x: xi, y: -nI }]);
  b.line([{ x: fi.t, y: nI }, { x: xi, y: nI }]);
  // the casing: a rectangle with cut corners
  const c = Math.min(0.18 * cw, 0.18 * Rv);
  b.line(
    [
      { x: A - cw / 2 + c, y: -Rv }, { x: A + cw / 2 - c, y: -Rv }, { x: A + cw / 2, y: -Rv + c },
      { x: A + cw / 2, y: Rv - c }, { x: A + cw / 2 - c, y: Rv }, { x: A - cw / 2 + c, y: Rv },
      { x: A - cw / 2, y: Rv - c }, { x: A - cw / 2, y: -Rv + c }
    ],
    true
  );
  // the outlet: neck, flange, bore and bolt holes, looking down the pipe
  b.circle({ x: A, y: 0 }, nw / 2, FINE);
  b.circle({ x: A, y: 0 }, fo.od / 2);
  b.circle({ x: A, y: 0 }, e.dnOut / 2, FINE);
  boltHoles(b, { x: A, y: 0 }, fo.pcd, fo.n, fo.hole);
  // the feet under the casing (hidden): two lugs, N1 apart over the outside, bolt holes N2 and M2 apart
  for (const sy of [-1, 1]) {
    const y0 = sy * (e.N1 / 2 - e.lug);
    const y1 = sy * (e.N1 / 2);
    b.rect(A - e.M1 / 2, Math.min(y0, y1), A + e.M1 / 2, Math.max(y0, y1), HIDDEN);
    for (const sx of [-1, 1]) b.circle({ x: A + (sx * e.M2) / 2, y: (sy * e.N2) / 2 }, e.boltHole / 2, HIDDEN);
  }
}

// ------------------------------------------------------------------------------------ CF series --

/** Where the drive end parts of a CF sit: fractions of F from the outlet centre line, taken from the catalogue drawing. */
function cfParts(r: CfRow) {
  const { xb } = ends({ dnIn: r.In, dnOut: r.Out, A: r.A, H1: r.H1, H2: r.H2, M1: r.M1, M2: r.M2, N1: r.N1, N2: r.N2, T1: r.T1, boltHole: r.S1 + 2, lug: r.B });
  const x0 = Math.max(xb, r.A + 0.17 * r.F);
  return {
    xb,
    flange0: x0,
    flange1: x0 + Math.max(12, 0.03 * r.F),
    bracket1: r.A + 0.44 * r.F,
    housing1: r.A + 0.77 * r.F,
    end: r.A + r.F,
    rbf: 0.63 * r.H1, // bracket flange half height
    rb: 0.55 * r.H1, // bracket body half height
    rh: 0.35 * r.H1, // bearing housing half height
    xsf: r.A + r.W // the support foot hole
  };
}

function cfBuild(r: CfRow, view: PumpView): { b: Builder; ports: FittingPortData[] } {
  const b = new Builder(`pump-cf-${r.In}x${r.Out}-${r.Imp}-${view}`);
  const e: PumpEnd = { dnIn: r.In, dnOut: r.Out, A: r.A, H1: r.H1, H2: r.H2, M1: r.M1, M2: r.M2, N1: r.N1, N2: r.N2, T1: r.T1, boltHole: r.S1 + 2, lug: r.B };
  const p = cfParts(r);
  const sh = CF_SHAFTS[r.shaft]!;
  const fi = flangeOf(r.In);
  const fo = flangeOf(r.Out);
  const keyX0 = p.end - sh.l + (sh.l - sh.keyLen) / 2;
  if (view === "side") {
    sidePumpEnd(b, e);
    // backplate, bracket flange, bracket, bearing housing, shaft
    b.rect(p.xb, -p.rb * 0.9, p.flange0, p.rb * 0.9);
    b.rect(p.flange0, -p.rbf, p.flange1, p.rbf);
    b.rect(p.flange1, -p.rb, p.bracket1, p.rb);
    b.rect(p.bracket1, -p.rh, p.housing1, p.rh);
    b.rect(p.housing1, -r.D / 2, p.end, r.D / 2);
    slot(b, keyX0, keyX0 + sh.keyLen, 0, sh.keyW);
    // the support foot under the bearing housing: hole S2, plate T2 thick
    const fl = 30;
    b.line([{ x: p.xsf - fl / 2, y: p.rh }, { x: p.xsf - fl / 2, y: r.H1 - r.T2 }, { x: p.xsf - fl, y: r.H1 - r.T2 }, { x: p.xsf - fl, y: r.H1 }, { x: p.xsf + fl, y: r.H1 }, { x: p.xsf + fl, y: r.H1 - r.T2 }, { x: p.xsf + fl / 2, y: r.H1 - r.T2 }, { x: p.xsf + fl / 2, y: p.rh }]);
    for (const sx of [-1, 1]) b.line([{ x: p.xsf + (sx * (r.S2 + 2)) / 2, y: r.H1 - r.T2 }, { x: p.xsf + (sx * (r.S2 + 2)) / 2, y: r.H1 }], false, HIDDEN);
    // the gap to keep clear at the drive end (X)
    b.rect(p.end, -p.rb, p.end + r.X, p.rb, GAP);
    // centre lines, and the labels at the flanges
    b.line([{ x: -30, y: 0 }, { x: p.end + 30, y: 0 }], false, CENTRE);
    b.line([{ x: r.A, y: -r.H2 - 25 }, { x: r.A, y: r.H1 + 25 }], false, CENTRE);
    b.textAt(r.A, -r.H2 + fo.t / 2, `OUT DN${r.Out}`, labelSize(fo.t));
    b.textAt(fi.t / 2, 0, `IN DN${r.In}`, labelSize(fi.t), true);
    return {
      b,
      ports: [
        { p: { x: 0, y: 0 }, dir: { x: -1, y: 0 }, size: r.In },
        { p: { x: r.A, y: -r.H2 }, dir: { x: 0, y: -1 }, size: r.Out }
      ]
    };
  }
  planPumpEnd(b, e);
  b.rect(p.xb, -p.rb * 0.9, p.flange0, p.rb * 0.9);
  b.rect(p.flange0, -p.rbf, p.flange1, p.rbf);
  b.rect(p.flange1, -p.rb, p.bracket1, p.rb);
  b.rect(p.bracket1, -p.rh, p.housing1, p.rh);
  b.rect(p.housing1, -r.D / 2, p.end, r.D / 2);
  slot(b, keyX0, keyX0 + sh.keyLen, 0, sh.keyW);
  // the support foot (hidden) with its one bolt hole
  b.rect(p.xsf - 30, -0.6 * p.rh, p.xsf + 30, 0.6 * p.rh, HIDDEN);
  b.circle({ x: p.xsf, y: 0 }, (r.S2 + 2) / 2, HIDDEN);
  b.rect(p.end, -p.rb, p.end + r.X, p.rb, GAP);
  b.line([{ x: -30, y: 0 }, { x: p.end + 30, y: 0 }], false, CENTRE);
  b.line([{ x: r.A, y: -ends(e).Rv - 25 }, { x: r.A, y: ends(e).Rv + 25 }], false, CENTRE);
  b.textAt(r.A, 0, `DN${r.Out}`, Math.max(10, Math.min(24, 0.22 * r.Out)));
  b.textAt(fi.t / 2, 0, `IN DN${r.In}`, labelSize(fi.t), true);
  return { b, ports: planPorts(r.A, r.In, r.Out) };
}

/**
 * The ends of a PLAN: the inlet at the flange face, and the outlet at the centre of the top flange. The outlet points up,
 * out of the plan: a VERTICAL port. A pipe on it runs in any direction on the plan and its end there is a floor
 * penetration circle. `dir` (towards -y) is only the direction Extend uses and the frame a moved pump turns its pipes with.
 */
function planPorts(A: number, dnIn: number, dnOut: number): FittingPortData[] {
  return [
    { p: { x: 0, y: 0 }, dir: { x: -1, y: 0 }, size: dnIn },
    { p: { x: A, y: 0 }, dir: { x: 0, y: -1 }, size: dnOut, vertical: true }
  ];
}

// ------------------------------------------------------------------------------------ CM series --

/** The parts of a CM that the table fixes (positions) or that are drawn from a proportion. */
function cmParts(r: CmRow) {
  const e: PumpEnd = { dnIn: r.In, dnOut: r.Out, A: r.H - r.E, H1: r.H1, H2: r.H2, M1: r.M1, M2: r.M2, N1: r.N1, N2: r.N2, T1: Math.min(25, Math.max(12, Math.round(0.085 * r.H1))), boltHole: r.S1, lug: r.N1 - r.N2 };
  const { xb } = ends(e);
  const xm0 = r.S - r.T; // the motor flange face
  const Rm = 0.97 * r.C; // the motor body radius, from the centre height
  const fan = 0.25 * r.T; // the fan cover
  return { e, xb, xm0, Rm, fan, ra: 0.55 * r.H1, tmf: Math.max(14, 0.06 * r.T), foot: Math.max(46, 3 * r.K) };
}

function cmBuild(r: CmRow, view: PumpView): { b: Builder; ports: FittingPortData[] } {
  const model = `${r.In}x${r.Out}-${r.Imp}`;
  const key = `pump-cm${r.poles}-${model}-${String(r.kW).replace(".", "_")}kw-${r.Frame.toLowerCase()}${r.note ? "-b" : ""}-${view}`;
  const b = new Builder(key);
  const p = cmParts(r);
  const e = p.e;
  const fi = flangeOf(r.In);
  const fo = flangeOf(r.Out);
  const motorEnd = r.S;
  const bodyEnd = motorEnd - p.fan;
  const flangeH = Math.min(p.Rm * 1.05, r.C); // the mounting flange is a plate: it stays above the motor feet
  if (view === "side") {
    sidePumpEnd(b, e);
    // adapter between the casing and the motor, with its support leg (L down from the shaft)
    b.rect(p.xb, -p.ra, p.xm0 - p.tmf, p.ra);
    const legW = Math.max(30, 0.35 * (p.xm0 - p.xb));
    const lx = (p.xb + p.xm0) / 2;
    if (r.L > p.ra) b.line([{ x: lx - legW / 2, y: p.ra }, { x: lx - legW / 2, y: r.L }, { x: lx + legW / 2, y: r.L }, { x: lx + legW / 2, y: p.ra }]);
    // the motor: flange, body, fan cover, fins, feet, terminal box
    b.rect(p.xm0 - p.tmf, -flangeH, p.xm0, flangeH);
    b.rect(p.xm0, -p.Rm, bodyEnd, p.Rm);
    b.line([{ x: bodyEnd, y: -0.88 * p.Rm }, { x: motorEnd - 0.06 * r.T, y: -0.88 * p.Rm }, { x: motorEnd, y: -0.7 * p.Rm }, { x: motorEnd, y: 0.7 * p.Rm }, { x: motorEnd - 0.06 * r.T, y: 0.88 * p.Rm }, { x: bodyEnd, y: 0.88 * p.Rm }]);
    for (const f of [-0.66, -0.33, 0, 0.33, 0.66]) b.line([{ x: p.xm0 + 0.06 * r.T, y: f * p.Rm }, { x: bodyEnd - 0.02 * r.T, y: f * p.Rm }], false, FINE);
    for (const hx of [r.H, r.H + r.B]) {
      b.rect(hx - p.foot / 2, p.Rm * 0.9, hx + p.foot / 2, r.C);
      b.line([{ x: hx - r.K / 2, y: p.Rm * 0.9 }, { x: hx - r.K / 2, y: r.C }], false, HIDDEN);
      b.line([{ x: hx + r.K / 2, y: p.Rm * 0.9 }, { x: hx + r.K / 2, y: r.C }], false, HIDDEN);
    }
    if (r.AD === null) b.rect(p.xm0 + 0.3 * r.T, -p.Rm * 1.35, p.xm0 + 0.55 * r.T, -p.Rm); // top terminal box
    else b.rect(p.xm0 + 0.3 * r.T, -0.45 * p.Rm, p.xm0 + 0.6 * r.T, 0.25 * p.Rm, FINE); // side terminal box, face on
    b.line([{ x: -30, y: 0 }, { x: motorEnd + 30, y: 0 }], false, CENTRE);
    b.line([{ x: e.A, y: -r.H2 - 25 }, { x: e.A, y: r.H1 + 25 }], false, CENTRE);
    b.textAt(e.A, -r.H2 + fo.t / 2, `OUT DN${r.Out}`, labelSize(fo.t));
    b.textAt(fi.t / 2, 0, `IN DN${r.In}`, labelSize(fi.t), true);
    return {
      b,
      ports: [
        { p: { x: 0, y: 0 }, dir: { x: -1, y: 0 }, size: r.In },
        { p: { x: e.A, y: -r.H2 }, dir: { x: 0, y: -1 }, size: r.Out }
      ]
    };
  }
  planPumpEnd(b, e);
  b.rect(p.xb, -p.ra, p.xm0 - p.tmf, p.ra);
  b.rect(p.xm0 - p.tmf, -flangeH, p.xm0, flangeH);
  b.rect(p.xm0, -p.Rm, bodyEnd, p.Rm);
  b.line([{ x: bodyEnd, y: -0.88 * p.Rm }, { x: motorEnd - 0.06 * r.T, y: -0.88 * p.Rm }, { x: motorEnd, y: -0.7 * p.Rm }, { x: motorEnd, y: 0.7 * p.Rm }, { x: motorEnd - 0.06 * r.T, y: 0.88 * p.Rm }, { x: bodyEnd, y: 0.88 * p.Rm }]);
  for (const f of [-0.66, -0.33, 0.33, 0.66]) b.line([{ x: p.xm0 + 0.06 * r.T, y: f * p.Rm }, { x: bodyEnd - 0.02 * r.T, y: f * p.Rm }], false, FINE);
  // the motor feet (hidden): N3 across the bolt holes, N4 over the outside
  const lug = r.N4 - r.N3;
  for (const sy of [-1, 1]) {
    for (const hx of [r.H, r.H + r.B]) {
      const y0 = sy * (r.N4 / 2 - lug);
      const y1 = sy * (r.N4 / 2);
      b.rect(hx - p.foot / 2, Math.min(y0, y1), hx + p.foot / 2, Math.max(y0, y1), HIDDEN);
      b.circle({ x: hx, y: (sy * r.N3) / 2 }, r.K / 2, HIDDEN);
    }
  }
  if (r.AD === null) b.rect(p.xm0 + 0.3 * r.T, -0.14 * r.T, p.xm0 + 0.55 * r.T, 0.14 * r.T, FINE);
  else b.rect(p.xm0 + 0.3 * r.T, p.Rm * 0.9, p.xm0 + 0.6 * r.T, r.AD); // side terminal box, on the +y side, AD from the shaft
  b.line([{ x: -30, y: 0 }, { x: motorEnd + 30, y: 0 }], false, CENTRE);
  b.line([{ x: e.A, y: -ends(e).Rv - 25 }, { x: e.A, y: ends(e).Rv + 25 }], false, CENTRE);
  b.textAt(e.A, 0, `DN${r.Out}`, Math.max(10, Math.min(24, 0.22 * r.Out)));
  b.textAt(fi.t / 2, 0, `IN DN${r.In}`, labelSize(fi.t), true);
  return { b, ports: planPorts(e.A, r.In, r.Out) };
}

// ------------------------------------------------------------------------------- the objects --

/** Bounds of the drawn geometry (texts are not counted: they sit inside or beside it). */
export function boundsOf(entities: Entity[]): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const add = (x: number, y: number) => {
    minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
  };
  for (const e of entities) {
    if (e.type === "polyline") e.points.forEach((p) => add(p.x, p.y));
    else if (e.type === "circle") { add(e.center.x - e.r, e.center.y - e.r); add(e.center.x + e.r, e.center.y + e.r); }
    else if (e.type === "arc") for (let i = 0; i <= 16; i++) { const a = rad(e.startDeg + (e.sweepDeg * i) / 16); add(e.center.x + e.r * Math.cos(a), e.center.y + e.r * Math.sin(a)); }
  }
  return { minX, minY, maxX, maxY };
}

const round3 = (v: number) => Math.round(v * 1000) / 1000;

function finish(made: { b: Builder; ports: FittingPortData[] }, info: PumpInfo, id: string, name: string, category: string): LibraryItem {
  const box = boundsOf(made.b.entities);
  const c = { x: (box.minX + box.maxX) / 2, y: (box.minY + box.maxY) / 2 };
  const mv = (p: Vec): Vec => ({ x: round3(p.x - c.x), y: round3(p.y - c.y) });
  const entities: Entity[] = made.b.entities.map((e) => {
    if (e.type === "polyline") return { ...e, points: e.points.map(mv) };
    if (e.type === "circle" || e.type === "arc") return { ...e, center: mv(e.center), r: round3(e.r) };
    if (e.type === "text") return { ...e, pos: mv(e.pos) };
    return e;
  });
  entities.push({
    id: `${id}-group`,
    type: "group",
    layerId: "pumps",
    locked: false,
    hidden: false,
    children: entities.map((e) => e.id),
    meta: { pump: info, ports: made.ports.map((pt) => ({ p: mv(pt.p), dir: { ...pt.dir }, size: pt.size, ...(pt.vertical ? { vertical: true } : {}) })) }
  });
  return { id, name, category, entities, size: { w: r1(box.maxX - box.minX), h: r1(box.maxY - box.minY) }, units: "mm", updatedAt: "2026-10-19T00:00:00.000Z" };
}

const VIEW_WORD: Record<PumpView, string> = { plan: "plan", side: "side" };

export function cfItem(r: CfRow, view: PumpView): LibraryItem {
  const model = `${r.In}x${r.Out}-${r.Imp}`;
  const made = cfBuild(r, view);
  const info: PumpInfo = {
    maker: "Davey", series: "CF", model, view, inletDn: r.In, outletDn: r.Out, massKg: r.kg, lengthMm: r.A + r.F,
    dims: { A: r.A, F: r.F, H1: r.H1, H2: r.H2, B: r.B, M1: r.M1, M2: r.M2, N1: r.N1, N2: r.N2, N3: r.N3, T1: r.T1, T2: r.T2, W: r.W, S1: r.S1, S2: r.S2, D: r.D, L: r.L, X: r.X, shaft: r.shaft }
  };
  const id = `builtin:pump-cf-${model}-${view}`;
  return finish(made, info, id, `Davey CF ${model} · ${VIEW_WORD[view]} · in DN${r.In} out DN${r.Out}`, PUMP_CATEGORIES.cf);
}

export function cmItem(r: CmRow, view: PumpView): LibraryItem {
  const model = `${r.In}x${r.Out}-${r.Imp}`;
  const made = cmBuild(r, view);
  const info: PumpInfo = {
    maker: "Davey", series: "CM", model, view, inletDn: r.In, outletDn: r.Out, kW: r.kW, frame: r.Frame, poles: r.poles, massKg: r.kg, lengthMm: r.S,
    dims: { N3: r.N3, B: r.B, C: r.C, E: r.E, H2: r.H2, H: r.H, J: r.J, K: r.K, L: r.L, H1: r.H1, N2: r.N2, N1: r.N1, S1: r.S1, N4: r.N4, S: r.S, T: r.T, M2: r.M2, M1: r.M1, ...(r.AD === null ? {} : { AD: r.AD }) },
    ...(r.note ? { note: r.note } : {})
  };
  // a note marks a second catalogue row, or (starting "Estimated") a row that is not in the catalogue
  const estimated = !!r.note && r.note.startsWith("Estimated");
  const key = `pump-cm${r.poles}-${model}-${String(r.kW).replace(".", "_")}kw-${r.Frame.toLowerCase()}${estimated ? "-est" : r.note ? "-b" : ""}-${view}`;
  const name = `Davey CM ${model} · ${r.kW} kW ${r.Frame}${estimated ? " (estimated, not in the catalogue)" : r.note ? " (catalogue row 2)" : ""} · ${VIEW_WORD[view]} · in DN${r.In} out DN${r.Out}`;
  return finish(made, info, `builtin:${key}`, name, r.poles === 2 ? PUMP_CATEGORIES.cm2 : PUMP_CATEGORIES.cm4);
}

/** The catalogue row of a placed pump (by series, model, power, frame, poles and note), or null. */
function rowOf(info: PumpInfo): { series: "CF"; row: CfRow } | { series: "CM"; row: CmRow } | null {
  if (info.series === "CF") {
    const row = cfRows().find((r) => `${r.In}x${r.Out}-${r.Imp}` === info.model);
    return row ? { series: "CF", row } : null;
  }
  const row = cmRows().find(
    (r) => `${r.In}x${r.Out}-${r.Imp}` === info.model && r.kW === info.kW && r.Frame === info.frame && r.poles === info.poles && (r.note ?? "") === (info.note ?? "")
  );
  return row ? { series: "CM", row } : null;
}

/**
 * A pump drawn at inlet size `dnIn` and outlet size `dnOut`, in the BUILDER frame (the inlet flange face at 0,0; not
 * centred like a library item): its entities in drawing order and its ports. Null when the row is not in the tables.
 * The same row and sizes always give the same entities in the same order.
 */
export function pumpRaw(info: PumpInfo, dnIn: number, dnOut: number): { entities: Entity[]; ports: FittingPortData[] } | null {
  const found = rowOf(info);
  if (!found) return null;
  const made = found.series === "CF" ? cfBuild({ ...found.row, In: dnIn, Out: dnOut }, info.view) : cmBuild({ ...found.row, In: dnIn, Out: dnOut }, info.view);
  return { entities: made.b.entities, ports: made.ports };
}

/** 3.16.0: the library item of a placed pump in the other view (plan or side), from its catalogue row; null when the row is not in the tables. */
export function pumpItemFor(info: PumpInfo, view: PumpView): LibraryItem | null {
  const found = rowOf(info);
  if (!found) return null;
  return found.series === "CF" ? cfItem(found.row, view) : cmItem(found.row, view);
}

/** The sizes a placed pump is drawn at: the changed sizes, else the catalogue sizes. */
export const drawnSizes = (info: PumpInfo): { dnIn: number; dnOut: number } => ({ dnIn: info.drawnIn ?? info.inletDn, dnOut: info.drawnOut ?? info.outletDn });

let cache: LibraryItem[] | null = null;

/** Every built-in pump: plan and side for each of the 141 pumps (one, CM 150x125-250 at 7.5 kW, is estimated). */
export function builtInPumps(): LibraryItem[] {
  if (cache) return cache;
  const out: LibraryItem[] = [];
  for (const r of cfRows()) for (const v of ["plan", "side"] as const) out.push(cfItem(r, v));
  for (const r of cmRows()) for (const v of ["plan", "side"] as const) out.push(cmItem(r, v));
  cache = out;
  return out;
}
